import { useState } from 'react'
import { X, AlertCircle, Loader } from 'lucide-react'
import { createLocation } from '../../services/settingsApi'

interface Props {
  apiKey: string
  onSuccess: (locationId: string) => void
  onClose: () => void
}

export default function AddLocationModal({ apiKey, onSuccess, onClose }: Props) {
  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const trimmedName = name.trim()
    if (!trimmedName) { setError('Location name is required.'); return }

    setIsSubmitting(true)
    try {
      const location = await createLocation(apiKey, { name: trimmedName, address: address.trim() })
      onSuccess(location.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add location. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-rd-surface border border-white/[0.07] rounded-2xl p-6 w-full max-w-md mx-4 space-y-5 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="font-sora font-semibold text-lg text-rd-text">Add a location</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-rd-muted hover:text-rd-text transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div className="space-y-1.5">
            <label className="block text-sm text-rd-muted" htmlFor="loc-name">
              Location name
            </label>
            <input
              id="loc-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Downtown Branch"
              autoComplete="off"
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-rd-text placeholder-rd-dim focus:outline-none focus:border-rd-accent/50 transition-colors"
            />
          </div>

          {/* Address */}
          <div className="space-y-1.5">
            <label className="block text-sm text-rd-muted" htmlFor="loc-address">
              Address <span className="text-rd-dim">(optional)</span>
            </label>
            <input
              id="loc-address"
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="123 Main St, Springfield"
              autoComplete="off"
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-rd-text placeholder-rd-dim focus:outline-none focus:border-rd-accent/50 transition-colors"
            />
          </div>

          <p className="text-xs text-rd-dim">
            You'll connect this location's Google Business Profile from Settings after it's created.
          </p>

          {/* Error */}
          {error && (
            <p className="text-sm text-danger-500 flex items-center gap-1.5">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm text-rd-muted hover:text-rd-text border border-white/10 hover:border-white/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #4ade80, #22d3ee)', color: '#0a0a0f' }}
            >
              {isSubmitting && <Loader className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {isSubmitting ? 'Adding…' : 'Add location'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
